/**
 * D549 (DS-CHARTS): the chart palette is redrawn from the Claude Design bar charts. The design's default bar is GRIDLESS and
 * coloured by an intensity RAMP (low = pale, peak = ink): the Admin A02 five-step gray ramp (`ramp.neutral`) and the
 * builder / Merchant three-step navy tint ramp (`ramp.tint`). The pale steps are decorative: a bar is always paired with its
 * value (tooltip, accessible summary, optional printed label), which is why `@vello/design-qa` exempts ONLY the ramp roles
 * from the 3:1 graphic floor. Series (lines, donut slices) keep the 3:1 floor.
 */
/** How many ordered series colours exist; a chart with more series cycles them (and varies the dash). */
export const CHART_SERIES_COUNT = 4;
export const LIGHT_CHART_COLORS = {
    'series.1': '#233048',
    'series.2': '#7A7E88',
    'series.3': '#394A68',
    'series.4': '#54575F',
    grid: '#EEEFF2',
    'grid.light': '#F5F6F8',
    label: '#7A7E88',
    value: '#233048',
    'ramp.neutral.1': '#E3E5E8',
    'ramp.neutral.2': '#D2D5DA',
    'ramp.neutral.3': '#A6AAB2',
    'ramp.neutral.4': '#54575F',
    'ramp.neutral.5': '#233048',
    'ramp.tint.1': '#C9D2E2',
    'ramp.tint.2': '#8FA0BE',
    'ramp.tint.3': '#233048',
    'bar.current': '#233048',
    'bar.rest': '#D2D5DA',
    'bar.empty': '#EEEFF2',
    axis: '#54575F',
    crosshair: '#A6AAB2',
    track: '#EEEFF2',
    'tooltip.surface': '#233048',
    'tooltip.text': '#FFFFFF',
    'heat.1': '#E9EAED',
    'heat.2': '#C6C9CF',
    'heat.3': '#9EA4AE',
    'heat.4': '#697283',
    'heat.5': '#354157',
    'heat.ink-low': '#233048',
    'heat.ink-high': '#FFFFFF',
    'band.1': '#C9CDD6',
    'band.2': '#DDE0E6',
    'band.3': '#EEEFF2',
    target: '#54575F',
};
export const DARK_CHART_COLORS = {
    'series.1': '#F5F6F8',
    'series.2': '#B4C0DA',
    'series.3': '#8C9DC0',
    'series.4': '#6F819F',
    grid: '#2B3A57',
    'grid.light': '#232F47',
    label: '#A6AAB2',
    value: '#F5F6F8',
    'ramp.neutral.1': '#2B3A57',
    'ramp.neutral.2': '#3F4F6E',
    'ramp.neutral.3': '#6F819F',
    'ramp.neutral.4': '#B4C0DA',
    'ramp.neutral.5': '#F5F6F8',
    'ramp.tint.1': '#3F4F6E',
    'ramp.tint.2': '#6F819F',
    'ramp.tint.3': '#F5F6F8',
    'bar.current': '#F5F6F8',
    'bar.rest': '#3F4F6E',
    'bar.empty': '#2B3A57',
    axis: '#A6AAB2',
    crosshair: '#7A7E88',
    track: '#2B3A57',
    'tooltip.surface': '#F5F6F8',
    'tooltip.text': '#233048',
    'heat.1': '#2D394F',
    'heat.2': '#434E61',
    'heat.3': '#5D6677',
    'heat.4': '#A3A8B2',
    'heat.5': '#DBDDE2',
    'heat.ink-low': '#F5F6F8',
    'heat.ink-high': '#233048',
    'band.1': '#4A5973',
    'band.2': '#38476A',
    'band.3': '#2B3A57',
    target: '#A6AAB2',
};
export const LIGHT_CHART_SCALARS = { areaOpacity: 0.1 };
export const DARK_CHART_SCALARS = { areaOpacity: 0.16 };
/** The ordered series roles, primary first. */
export const CHART_SERIES_ROLES = ['series.1', 'series.2', 'series.3', 'series.4'];
/** The neutral (Admin A02) value ramp roles, lowest value first (D549). */
export const CHART_RAMP_NEUTRAL_ROLES = ['ramp.neutral.1', 'ramp.neutral.2', 'ramp.neutral.3', 'ramp.neutral.4', 'ramp.neutral.5'];
/** The tint (report builder / Merchant) value ramp roles, lowest value first (D549). */
export const CHART_RAMP_TINT_ROLES = ['ramp.tint.1', 'ramp.tint.2', 'ramp.tint.3'];
/** Every decorative ramp role (exempt from the 3:1 graphic floor in `@vello/design-qa`, and only these). */
export const CHART_RAMP_ROLES = [...CHART_RAMP_NEUTRAL_ROLES, ...CHART_RAMP_TINT_ROLES, 'bar.rest', 'bar.empty'];
/** The heat scale roles, lowest value first (D529). */
export const CHART_HEAT_ROLES = ['heat.1', 'heat.2', 'heat.3', 'heat.4', 'heat.5'];
/** The bullet-gauge band roles, lowest range first (D529). */
export const CHART_BAND_ROLES = ['band.1', 'band.2', 'band.3'];
/** The semantic tones a chart may use to mark a STATE, and the (accessible) colour role each one aliases. */
export const CHART_TONE_ROLES = {
    success: 'success.text',
    warning: 'warning.text',
    danger: 'danger.text',
};
/** The CSS custom property of a chart colour role (`series.1` → `--chart-series-1`). */
export function chartColorVar(role) {
    return `--chart-${role.replace(/\./g, '-')}`;
}
/** The CSS custom property of the chart tone alias (`danger` → `--chart-tone-danger`). */
export function chartToneVar(tone) {
    return `--chart-tone-${tone}`;
}
// ── Geometry and motion (D549) ───────────────────────────────────────────────────────────────────────────────────────────
/** The design's bar geometry, in CSS px: the gap between bars, the radius of a bar's top corners, and the default plot heights. */
export const CHART_BAR = {
    /** Admin A02 / Merchant MRC-014: 6px between bars (the builder preview uses 5, the driver week 10). */
    gapPx: 6,
    /** `4px 4px 0 0` (A02, MRC-014); the builder preview is 3, the driver week and the TV 6. */
    radiusPx: 4,
    /** The compact design plot (MRC-014 uses 120, A02 fills its card, the builder preview is 100). */
    designHeightPx: 120,
    /** The analytic (axis + grid) plot. */
    analyticHeightPx: 220,
};
/** The track heights of a progress bar / meter (`xs` 4 = rider flow, `sm` 6 = A07 challenge, `md` 8 = M13 challenge, `lg` 10 = TV city). */
export const CHART_TRACK_PX = { xs: 4, sm: 6, md: 8, lg: 10 };
/** The chart-layer motion, in ms (FX-05 count-up, FX-07 cascade, FX-06 sweep / pulse, FX-16 route draw). */
export const CHART_MOTION_MS = {
    /** FX-05: the KPI count-up lasts 400ms with an ease-out cubic (`1 - (1 - p)^3`). */
    countUp: 400,
    /** Entrance of a bar / line / ring / progress fill. */
    entrance: 320,
    /** A change of data tweens old → new in this time. */
    tween: 320,
    /** FX-07: the step of the cascade (60ms), capped at {@link CHART_STAGGER_MAX_STEPS} items. */
    stagger: 60,
    /** FX-06: the progress highlight sweep (linear, infinite). */
    sweep: 1800,
    /** FX-06: the live-dot pulse (infinite). */
    pulse: 2000,
    /** FX-16: the route line draws in. */
    routeDraw: 900,
    /** FX-15 / the stepper line fills. */
    stepFill: 400,
};
/** FX-07: items past the sixth enter together with it. */
export const CHART_STAGGER_MAX_STEPS = 6;
/**
 * The KPI tile hover (every tile lifts, not only a clickable one): the rise. The hover border (`--color-border-kpi-hover`),
 * shadow (`--shadow-kpi`) and 160 ms (`--duration-card`) are D547 role tokens, themed for dark; they are not repeated here.
 */
export const KPI_HOVER = {
    liftPx: 2,
};
/** FX-06 (the live pulse ring and the progress highlight sweep): raw colours live here, `charts.css` only references the variables. */
export const CHART_LIVE = {
    /** The pulse ring starts at brand green 50 % and clears to transparent while it grows 9px. */
    pulseRing: 'rgba(16, 197, 84, 0.5)',
    pulseRingClear: 'rgba(16, 197, 84, 0)',
    /** The travelling highlight of a live progress fill: transparent to white 35 % to transparent. */
    sweepHighlight: 'rgba(255, 255, 255, 0.35)',
    sweepClear: 'rgba(255, 255, 255, 0)',
    /** The 3px ring around a route endpoint (FX-16). */
    routeRing: '#FFFFFF',
};
/**
 * The theme-invariant chart variables (geometry, motion, KPI hover), emitted by `cssVariablesFor` next to the colours so
 * `charts.css` never carries a literal.
 */
export function chartStaticVariables() {
    return {
        '--chart-bar-gap': `${CHART_BAR.gapPx}px`,
        '--chart-bar-radius': `${CHART_BAR.radiusPx}px`,
        '--chart-track-xs': `${CHART_TRACK_PX.xs}px`,
        '--chart-track-sm': `${CHART_TRACK_PX.sm}px`,
        '--chart-track-md': `${CHART_TRACK_PX.md}px`,
        '--chart-track-lg': `${CHART_TRACK_PX.lg}px`,
        '--chart-countup-duration': `${CHART_MOTION_MS.countUp}ms`,
        '--chart-entrance-duration': `${CHART_MOTION_MS.entrance}ms`,
        '--chart-stagger': `${CHART_MOTION_MS.stagger}ms`,
        '--chart-sweep-duration': `${CHART_MOTION_MS.sweep}ms`,
        '--chart-pulse-duration': `${CHART_MOTION_MS.pulse}ms`,
        '--chart-route-duration': `${CHART_MOTION_MS.routeDraw}ms`,
        '--chart-step-duration': `${CHART_MOTION_MS.stepFill}ms`,
        '--chart-pulse-ring': CHART_LIVE.pulseRing,
        '--chart-pulse-ring-clear': CHART_LIVE.pulseRingClear,
        '--chart-sweep-highlight': CHART_LIVE.sweepHighlight,
        '--chart-sweep-clear': CHART_LIVE.sweepClear,
        '--chart-route-ring': CHART_LIVE.routeRing,
        '--kpi-hover-lift': `-${KPI_HOVER.liftPx}px`,
    };
}
//# sourceMappingURL=charts.js.map